/**
 * Platform-admin domain API functions (Step 7.1.13).
 *
 * Platform-level endpoints that operate ABOVE organization boundaries:
 * org management, impersonation, platform-wide audit logs.
 * Extracted from adminData/index.js for team scalability.
 */
import {
  API_BASE_URL,
  getApiHeaders,
  parseAdminErrorResponse,
  parseAdminErrorFromText,
} from "./core";

/** GET /api/platform/organizations — platform_admin only */
export async function fetchPlatformOrganizations() {
  const res = await fetch(`${API_BASE_URL}/api/platform/organizations`, {
    headers: getApiHeaders(),
    cache: "no-store",
  });
  if (!res.ok) {
    throw new Error(await parseAdminErrorResponse(res));
  }
  const data = await res.json();
  return Array.isArray(data) ? data : [];
}

/** GET /api/platform/organizations/{id} — platform_admin only */
export async function fetchPlatformOrganization(organizationId) {
  const id = encodeURIComponent(organizationId);
  const res = await fetch(`${API_BASE_URL}/api/platform/organizations/${id}`, {
    headers: getApiHeaders(),
  });
  if (!res.ok) {
    throw new Error(await parseAdminErrorResponse(res));
  }
  return res.json();
}

/** POST /api/platform/organizations — platform_admin only */
export async function createPlatformOrganization(body) {
  // eslint-disable-next-line no-console -- temporary [ORG_API] proof logs; remove after confirming runtime chain
  console.log("[ORG_API] entered createPlatformOrganization");

  const nativeFetch = window.__nativeFetch || fetch;
  const res = await nativeFetch(`${API_BASE_URL}/api/platform/organizations`, {
    method: "POST",
    headers: getApiHeaders(),
    body: JSON.stringify(body),
  });

  // eslint-disable-next-line no-console -- temporary [ORG_API] proof logs
  console.log("[ORG_API] response status/statusText", res.status, res.statusText);

  let text = "";
  try {
    text = await res.text();
    // eslint-disable-next-line no-console -- temporary [ORG_API] proof logs
    console.log("[ORG_API] raw text if readable", text);
  } catch (readErr) {
    // eslint-disable-next-line no-console -- temporary [ORG_API] proof logs
    console.log("[ORG_API] raw text if readable", "<res.text() threw>", readErr);
  }

  if (!res.ok) {
    const parsed = parseAdminErrorFromText(text, res.status, res.statusText);
    // eslint-disable-next-line no-console -- temporary [ORG_API] proof logs
    console.log("[ORG_API] parsed error string", parsed);
    // eslint-disable-next-line no-console -- temporary [ORG_API] proof logs
    console.log("[ORG_API] exact string passed to throw new Error(...)", parsed);
    throw new Error(parsed);
  }

  return text ? JSON.parse(text) : null;
}

/** Shared query string for platform audit list + CSV export (filters only). */
function buildPlatformAuditLogFilterParams({
  q,
  action,
  entityType,
  organizationId,
  actor,
  dateFrom,
  dateTo,
}) {
  const params = new URLSearchParams();
  if (q != null && String(q).trim() !== "") params.set("q", String(q).trim());
  if (action != null && String(action).trim() !== "") params.set("action", String(action).trim());
  if (entityType != null && String(entityType).trim() !== "") {
    params.set("entity_type", String(entityType).trim());
  }
  if (organizationId != null && String(organizationId).trim() !== "") {
    params.set("organization_id", String(organizationId).trim());
  }
  if (actor != null && String(actor).trim() !== "") params.set("actor", String(actor).trim());
  if (dateFrom != null && String(dateFrom).trim() !== "") params.set("date_from", String(dateFrom).trim());
  if (dateTo != null && String(dateTo).trim() !== "") params.set("date_to", String(dateTo).trim());
  return params;
}

/** GET /api/platform/audit-logs — platform_admin only; paginated + filterable audit feed. */
export async function fetchPlatformAuditLogs({
  page = 1,
  pageSize = 25,
  q,
  action,
  entityType,
  organizationId,
  actor,
  dateFrom,
  dateTo,
} = {}) {
  const params = buildPlatformAuditLogFilterParams({
    q,
    action,
    entityType,
    organizationId,
    actor,
    dateFrom,
    dateTo,
  });
  params.set("page", String(page));
  params.set("page_size", String(pageSize));
  const res = await fetch(`${API_BASE_URL}/api/platform/audit-logs?${params.toString()}`, {
    headers: getApiHeaders(),
    credentials: "include",
  });
  if (!res.ok) {
    throw new Error(await parseAdminErrorResponse(res));
  }
  return res.json();
}

/** GET /api/platform/audit-logs/export — all rows matching current filters (CSV). */
export async function exportPlatformAuditLogs({
  q,
  action,
  entityType,
  organizationId,
  actor,
  dateFrom,
  dateTo,
} = {}) {
  const params = buildPlatformAuditLogFilterParams({
    q,
    action,
    entityType,
    organizationId,
    actor,
    dateFrom,
    dateTo,
  });
  const res = await fetch(`${API_BASE_URL}/api/platform/audit-logs/export?${params.toString()}`, {
    headers: getApiHeaders(),
    credentials: "include",
  });
  if (!res.ok) {
    throw new Error(await parseAdminErrorResponse(res));
  }
  return res.blob();
}

/** POST /api/platform/impersonate/{organizationId} — platform_admin only; returns new access_token (support mode). */
export async function platformImpersonate(organizationId) {
  const id = encodeURIComponent(organizationId);
  const res = await fetch(`${API_BASE_URL}/api/platform/impersonate/${id}`, {
    method: "POST",
    headers: getApiHeaders(),
    credentials: "include",
  });
  if (!res.ok) {
    throw new Error(await parseAdminErrorResponse(res));
  }
  return res.json();
}
